import {
  Button,
  DatePicker,
  Divider,
  Input,
  message,
  Radio,
  Select,
  Upload,
} from "antd";
import { Col, Form, Row } from "antd";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { IDocumentFields } from "../../../../interfaces/ICase";
import dayjs from "dayjs";
import { fetchPreAuthIhxToPortal } from "../../../../apis/PreAuthService";
import {
  deleteUploadedDocument,
  downloadDocument,
  getAllStarhealthDocumentsData,
  submitApproval,
  submitApprovalIhx,
  updateAllDocumentsData,
} from "../../../../apis/patientService";
import { errorMessage } from "../../../../utils/notificationUtils";
import {
  getBasicDetails,
  getDeficiencyQuestionData,
} from "../../../../models/patientSlice";
import {
  getPortalDataIhx,
  getVisitStageStates,
  postPreAuthData,
  updatePreAuthData,
} from "../../../../models/preAuthSlice";
import FilePreviewModal from "../../PreAuth-query/FilePreviewModal";
import { UploadDocument } from "../../../../models/EnhancementSlice";
import PatientListPreviewDocuments from "../../../modals/PatientListPreviewDocuments";
import PatientDetailsStageDocument from "../../../modals/PatientDetailsStageDocument";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";
import MemberDetails from "./IhxTabs/MemberDetails";
import TreatmentDetails from "./IhxTabs/TreatmentDetails";
import DocumentDeficiencyModal from "../../DocumentDeficiencyModal";
import BillDetails from "./IhxTabs/BillDetails";
import DocumentUploadIhx from "./IhxTabs/DocumentUploadIhx";
import ResponseDocumentUploadIhx from "./IhxTabs/ResponseDocumentUploadIhx";
import ConfirmSubmissionModalStage from "../../../modals/ConfirmSubmissionModalStage";
import Loader from "../../../../utils/Spinners";
import { Spin } from "antd";
import ManuallyClaimIdPortal from "../ManuallyClaimIdPortal";
import MultipleFilesPreviewModal from "../../PreAuth-query/MultipleFilesPreviewModal";

const { Option } = Select;

type PreAuthIhxProps = {
  diagnosisOptions: any;
  procedureOptions: any;
  roomTypeOptions: any;
  treatmentOptions: any;
  preAuthData: any;
  fetchPreAuthData: any;
  generalRoomTypeOptions: any;
  proposedLineOfTreatment: any;
  fetchStepperForStatus: any;
  getTimelineDetail: any;
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

export interface FormErrors {
  [key: string]: string;
}

// Define interface for payload
interface PayloadTreatment {
  id: number;
  name: string;
}

interface PayloadDiagnosis {
  id: number;
  name: string;
}

interface PayloadRoomType {
  id: number;
  name: string;
}

interface TreatmentField {
  isDropdown?: boolean;
  possibleValues?: string | null;
  fieldName?: string;
  isPredefined?: boolean;
  predefinedValue?: string | null;
  treatment?: string | null;
  isDate?: boolean;
}

interface Treatment {
  id: string;
  portal?: string;
  gsigmaPayerId?: number;
  lineOfTreatment?: {
    portal?: string;
    gsigmaPayerId?: number;
    lineOfTreatment?: string;
    lineOfTreatmentId?: number;
  };
  treatment: string;
  ihx_treatment_id?: string;
  ihxLineOfTreatmentId?: string | null;
  treatmentFields?: TreatmentField[];
}

interface LineOfTreatmentData {
  treatments: Treatment[];
}

interface PayloadProcedure {
  id: number;
  name: string;
}

interface CasePreAuthIhxRequest {
  casePreAuthRequest: {
    patientOpdIpdNumber: string;
    beneficiaryId: string;
    total: string;
  };
  // total: string;
  documentURLs: any;
  isCovidCase: string;
  proposedLineOfTreatment: string;
  treatment: PayloadTreatment;
  diagnosis: PayloadDiagnosis[];
  roomType: PayloadRoomType;
  procedure: PayloadProcedure[];
  Approach: string;
  "number of dialysis sessions claimed": string;
  otherHospitalExpense: string;
  roomRentAndNursingCharges: string;
  remarks: string;
  hospitalId: number;
  schemeId: number;
}

export interface Document {
  id: number;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  labelId?: string | number;
  documentName?: string;
  requestDocument?: boolean;
  responseDocument?: boolean;
  multiple?: boolean;
}

interface FormPayload {
  schemeMetaDataId: number;
  portalMetaDataId: number;
  casePreAuthId: number;
  casePreAuthIhxRequest: CasePreAuthIhxRequest;
  casePreAuthCghsRequest: null;
  casePreAuthVidalRequest: null;
  casePreAuthStarhealthRequest: null;
  casePreAuthEchsRequest: null;
  action: string;
}

const PreAuthIhx: FC<PreAuthIhxProps> = ({
  diagnosisOptions,
  roomTypeOptions,
  procedureOptions,
  treatmentOptions,
  preAuthData,
  fetchPreAuthData,
  generalRoomTypeOptions,
  proposedLineOfTreatment,
  fetchStepperForStatus,
  getTimelineDetail,
  activeKey,
  sseData,
  sseUpdateCount
}) => {
  const [form] = Form.useForm();
  const [showPanCard, setShowPanCard] = useState(false);
  const { hasFunctionality } = useUserFunctionalities();

  const dispatch = useDispatch<AppDispatch>();
  const [LINE_OF_TREATMENT_MAP, setLineOfTreatmentMap] = useState<{
    [key: string]: string;
  }>();

  const { enhancement, hospital, patients, auth, preAuth } = useSelector(
    (state: RootState) => state
  );
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [manuallyClaimIdPortal, setManuallyClaimIdPortal] =
    useState<boolean>(false);
  const [formData, setFormData] = useState<any>({
    // Step 1 - Member Details
    memberId: patients?.fetchBasicDetails?.casesResponse?.memberId || "",
    patientOpdIpdNumber:
      patients?.fetchBasicDetails?.visitInfoResponse?.opNumber || "",
    panCardNumber: "",

    // Step 2 - Treatment Details
    isCovidCase: "no",
    proposedLineOfTreatment: "",
    treatment: "",
    diagnosis: [], // Initialize as empty array
    diagnosisIds: [], // Initialize as empty array
    procedure: [], // Initialize as empty array
    procedureIds: [],
    roomType: "",
    otherRoomType: "",
    otherRoomTypeId: "",
    roomId: "",
    treatmentName: "",
    treatmentId: "",
    dod: null,

    // Step 3 - Bill Details
    package: "",
    roomRentAndNursingCharges: "",
    consultation: "",
    otCharges: "",
    surgeonAndAnesthetist: "",
    investigation: "",
    medicineAndConsumables: "",
    otherHospitalExpense: "",

    // Step 4 - Document Upload
    documents: [],
    remarks: "",
  });
  const [documentUrls, setDocumentUrls] = useState<
    {
      documentType: string;
      documentName: string;
      documentUrl: string;
      id: number;
      referenceType: string | null;
      documentLabelType: any;
      deficiencyCheckStatus: any;
      remarks: string | null;
    }[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); //for submit on portal
  const [isIntimating, setIsIntimating] = useState<boolean>(false); //for intimation of preauth button
  const [isSaving, setIsSaving] = useState<boolean>(false); //for saving and updating as draft

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName
        ?.toLowerCase()
        ?.includes("aditya birla")
    ) {
      setShowPanCard(true);
    }
  }, [patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName]);

  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [documentTypes, setDocumentTypes] = useState<Document[]>([]);
  const [
    documentRequestResponseDocuments,
    setDocumentRequestResponseDocuments,
  ] = useState<any>();
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [previewDocument, setPreviewDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [updateButton, setUpdateButton] = useState(false);
  const [fetchingDeficiencyData, setFetchingDeficiencyData] =
    useState<boolean>(false);
  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;

  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);

  const [treatmentDataOptions, setTreatmentDataOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] =
    useState<any>();

  const [selectedFiles, setSelectedFiles] = useState<
    { file: File; documentType: string }[]
  >([]);
  const [previewFiles, setPreviewFiles] = useState<
    {
      url: string;
      name: string;
      showUpload: boolean;
      documentType: string;
      isMultiple: boolean;
    }[]
  >([]);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);

  useEffect(() => {
    if (!previewModalVisible) {
      setSelectedFiles([]);
    }
  }, [previewModalVisible]);

  // Mapping of line of treatment to its key in TREATMENT_DATA
  //   const LINE_OF_TREATMENT_MAP: { [key: string]: string } = {
  //     "Medical Management::1112": "Medical Management::1112",
  //     "Investigation::1114": "Investigation::1114",
  //     "Intensive Care::1113": "Intensive Care::1113",
  //     "Surgical Management::1123": "Surgical Management::1123",
  //   };

  useEffect(() => {
    const newData: Record<string, string> = {};
    proposedLineOfTreatment?.forEach((item: any) => {
      newData[item?.value] = item?.value;
    });
    setLineOfTreatmentMap(newData);
  }, [proposedLineOfTreatment]);

  const currentTreatmentFields = useMemo(() => {
    if (
      !formData.proposedLineOfTreatment ||
      !formData.treatment ||
      !treatmentOptions
    )
      return [];
    if (
      formData.proposedLineOfTreatment &&
      formData.treatment &&
      typeof treatmentOptions == "object"
    ) {
      const lineOfTreatmentKey =
        LINE_OF_TREATMENT_MAP?.[formData.proposedLineOfTreatment];
      if (lineOfTreatmentKey) {
        const treatment = treatmentOptions[lineOfTreatmentKey]?.treatments.find(
          (t: any) =>
            t.id === formData.treatment || t.id == formData.treatmentId
        );

        return treatment?.treatmentFields || [];
      }
    }
  }, [formData.proposedLineOfTreatment, formData.treatment, treatmentOptions]);

  useEffect(() => {
    if (formData.proposedLineOfTreatment && treatmentOptions) {
      const lineOfTreatmentKey =
        LINE_OF_TREATMENT_MAP?.[formData.proposedLineOfTreatment];
      if (
        formData.proposedLineOfTreatment &&
        lineOfTreatmentKey &&
        treatmentOptions[lineOfTreatmentKey]
      ) {
        const options = treatmentOptions[
          formData.proposedLineOfTreatment
        ].treatments?.map((treatment: any) => ({
          value: treatment.id,
          label: treatment.treatment,
        }));
        setTreatmentDataOptions(options);
      } else {
        setTreatmentDataOptions([]);
      }
    }
  }, [formData.proposedLineOfTreatment, treatmentOptions]);

  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as Document[],
      responseDocuments: [] as Document[],
    };

    documentTypes?.forEach((document) => {
      if (document?.requestDocument) {
        requestResponseDocuments.requestedDocuments.push(document);
      } else if (document?.responseDocument) {
        requestResponseDocuments.responseDocuments.push(document);
      }
    });

    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  const fetchDataForSse = async () => {
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      // dispatch(
      //   getBasicDetails({
      //     id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      //   })
      // );
    }
    await fetchPreAuthData();
    fetchStepperForStatus();
    await getTimelineDetail();
    fetchDocuments();
  };

  useEffect(() => {
    if (sseData) {
      fetchDataForSse();
    }
  }, [sseData, sseUpdateCount]);

  // useEffect(() => {
  //   const requestResponseDocuments = documentTypes?.reduce(
  //     (acc: any, document: Document) => {
  //       if (document?.requestDocument) {
  //         if (acc["requestedDocuments"]) {
  //           acc["requestedDocuments"].push(document);
  //         } else {
  //           acc["requestedDocuments"] = [document];
  //         }
  //       } else if (document?.responseDocument) {
  //         if (acc["responseDocuments"]) {
  //           acc["responseDocuments"].push(document);
  //         } else {
  //           acc["responseDocuments"] = [document];
  //         }
  //       }

  //       return acc;
  //     },
  //     {}
  //   );

  //   setDocumentRequestResponseDocuments(requestResponseDocuments);
  // }, [documentTypes]);

  const updateOrAddDocument = (
    uploadedDocumentUrl: any,
    documentRes: IDocumentFields
  ) => {
    const updatedDocuments = [...uploadedDocumentUrl];

    updatedDocuments.push({
      documentType: documentRes?.documentType,
      documentName: documentRes?.documentName,
      documentUrl: documentRes?.documentUrl,
      documentLabelType: documentRes?.documentLabelType,
      id: documentRes?.id,
      deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
    });

    // Return the updated array (ensures it's a flat array)
    return updatedDocuments;
  };

  const handleUpload = async () => {
    if (selectedFiles?.length > 0) {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
        );
      }
      CollectPostData.append("documentType", previewFiles?.[0]?.documentType);

      selectedFiles?.map((fileObj, index) =>
        CollectPostData.append(`file${index}`, fileObj.file)
      );

      // if (selectedFiles?.length > 0) {
      //   selectedFiles.forEach((fileObj, index) => {
      //     // Extract the actual File object from your structure
      //     const actualFile = fileObj.file; // This is the originFileObj (actual File)

      //     if (actualFile instanceof File) {
      //       CollectPostData.append("files", actualFile);
      //     }
      //   });
      // }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          })
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`
        );
        await handleAttachDocuments(documentRes);
        setUploadFileList([]);
        setSelectedFiles([]);
        setPreviewFiles([]);
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    } else {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString()
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
        );
      }
      CollectPostData.append("documentType", previewFile.documentType);

      if (selectedFile?.file) {
        CollectPostData.append("file", selectedFile?.file);
      }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          })
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`
        );
        await handleAttachDocuments(documentRes);
        setSelectedFile(null);
        setPreviewFile({
          url: "",
          name: "",
          showUpload: false,
          documentType: "",
        });
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    }
  };

  // const handleUpload = async () => {
  //   const CollectPostData = new FormData();
  //   if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
  //     CollectPostData.append(
  //       "hospitalId",
  //       patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.patientResponse?.id) {
  //     CollectPostData.append(
  //       "patientId",
  //       patients?.fetchBasicDetails?.patientResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.casesResponse?.id) {
  //     CollectPostData.append(
  //       "caseId",
  //       patients?.fetchBasicDetails?.casesResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
  //     CollectPostData.append(
  //       "visitId",
  //       patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
  //     );
  //   }
  //   CollectPostData.append("documentType", previewFile.documentType);

  //   if (selectedFile?.file) {
  //     CollectPostData.append("file", selectedFile?.file);
  //   }

  //   try {
  //     const documentRes = await dispatch(
  //       UploadDocument({
  //         formData: CollectPostData,
  //         accessToken: auth.accessToken || "",
  //       })
  //     ).unwrap();

  //     const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);

  //     setDocumentUrls(updatedDocuments);
  //     setPreviewModalVisible(false);
  //     message.success(
  //       `${documentRes?.documentLabelType} uploaded successfully`
  //     );
  //   } catch (error) {
  //     console.error("Failed to upload document:", error);
  //   }
  // };

  const totalFields = [
    "package",
    "roomRentAndNursingCharges",
    "consultation",
    "otCharges",
    "surgeonAndAnesthetist",
    "investigation",
    "medicineAndConsumables",
    "otherHospitalExpense",
  ];

  //to check only number should be value
  function validateInputOnlyNumeric(input: string) {
    // if (input.includes("  ")) {
    //   return false;
    // }

    // // Check for special characters
    // if (/[^0-9\s]/.test(input)) {
    //   return false;
    // }

    // return true;

    if (input === "") return true;
    return /^[0-9]+$/.test(input);
  }

  // Handle input changes
  const handleInputChange = (
    field: any,
    value: any,
    additionalData?: any
  ): void => {
    if (totalFields?.includes(field)) {
      if (!validateInputOnlyNumeric(value)) {
        setErrors({
          ...errors,
          [field]: "Only Numbers are allowed",
        });

        return;
      } else {
        setErrors({
          ...errors,
          [field]: "",
        });
      }
    }
    const updatedFormData = {
      ...formData,
      [field]: value,
    };

    if (field === "dod") {
      updatedFormData.dod = value
        ? dayjs(value).format("DD-MM-YYYY HH:mm:ss")
        : null;
    }
    if (field === "diagnosis") {
      updatedFormData.diagnosis = value; // Array of selected labels
      updatedFormData.diagnosisIds = additionalData
        ?.filter((item: any) => value.includes(item.label)) // Only keep IDs for selected labels
        ?.map((item: any) => item.key);
    }
    if (field === "procedure") {
      updatedFormData.procedure = value; // Array of selected labels
      updatedFormData.procedureIds = additionalData
        ?.filter((item: any) => value.includes(item.label)) // Only keep IDs for selected labels
        ?.map((item: any) => item.key);
    }

    if (field === "roomType") {
      updatedFormData.roomType = value; // Array of selected labels
      updatedFormData.roomId = additionalData.key;
    }
    if (field === "otherRoomType") {
      updatedFormData.otherRoomType = value; // Array of selected labels
      updatedFormData.otherRoomId = additionalData.key;
    }

    // Special handling for proposed line of treatment
    if (field === "proposedLineOfTreatment") {
      // Reset treatment when line of treatment changes
      updatedFormData.treatment = "";
      currentTreatmentFields.forEach((field: any) => {
        if (field.fieldName) {
          delete updatedFormData[field.fieldName];
        }
      });
      // Get treatment options based on selected line of treatment
      const lineOfTreatmentKey = LINE_OF_TREATMENT_MAP?.[value];
      if (
        value &&
        lineOfTreatmentKey &&
        treatmentOptions?.[lineOfTreatmentKey]
      ) {
        const options = treatmentOptions[value].treatments?.map(
          (treatment: any) => ({
            value: treatment.id,
            label: treatment.treatment,
          })
        );
        setTreatmentDataOptions(options);
      } else {
        setTreatmentDataOptions([]);
      }
    }

    if (field === "treatment") {
      // Remove previous treatment's fields
      currentTreatmentFields.forEach((field: any) => {
        if (field.fieldName) {
          const fieldKey = field.fieldName
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");
          delete updatedFormData?.dynamicFields[fieldKey];
        }
      });
      updatedFormData.treatmentId = additionalData.key;
      updatedFormData.treatmentName = additionalData.treatment;
    }

    // Update form data
    setFormData(updatedFormData);

    // Clear error for this field when user interacts
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const handleFileChange =
    (documentType: string, isMultiple: boolean) => (e: any) => {
      if (isMultiple) {
        const files = e?.fileList || [];
        setUploadFileList(files);
        if (files?.length === 0) return;

        const selected = files?.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }));

        const previews = files?.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
          isMultiple: isMultiple,
        }));

        setSelectedFiles(selected);
        setPreviewFiles(previews);
        setPreviewModalVisible(true);
      } else {
        if (Array.isArray(e)) {
          return e;
        }
        if (e?.file) {
          setSelectedFile({ file: e.file, documentType: documentType });
          setPreviewFile({
            url: URL.createObjectURL(e.file),
            name: e.file.name,
            showUpload: true,
            documentType: documentType,
          });
          setPreviewModalVisible(true);
        }
        return e?.target?.files;
      }
    };

  // const handleFileChange = (documentType: string) => (e: any) => {
  //   const maxSize = 15 * 1024 * 1024;

  //   if (Array.isArray(e)) {
  //     return e;
  //   }
  //   if (e?.file) {
  //     setSelectedFile({ file: e.file, documentType: documentType });
  //     setPreviewFile({
  //       url: URL.createObjectURL(e.file),
  //       name: e.file.name,
  //       showUpload: true,
  //       documentType: documentType,
  //     });
  //     setPreviewModalVisible(true);
  //   }
  //   return e?.target?.files;
  // };

  const renderTreatmentField = (field: {
    isDropdown: boolean;
    possibleValues: any;
    fieldName: string;
    isPredefined: boolean;
    predefinedValue: any;
    treatment: string | null;
    isDate: boolean;
  }) => {
    // Ensure dynamicFields exists
    if (!formData.dynamicFields) {
      setFormData((prev: any) => ({
        ...prev,
        dynamicFields: {},
      }));
    }

    // Convert field name to a key-friendly format
    const fieldKey: any = field.fieldName
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

    const errorMessage = errors.dynamicFields && errors.dynamicFields[fieldKey];

    // Render based on field type
    if (field.isDropdown && field.possibleValues) {
      // Dropdown field
      const options = field.possibleValues.split(":")?.map((value: any) => ({
        value: value.trim(),
        label: value.trim(),
      }));
      return (
        <>
          <Select
            value={formData.dynamicFields?.[fieldKey] || null}
            onChange={(value) => {
              setFormData((prev: any) => ({
                ...prev,
                dynamicFields: {
                  ...prev.dynamicFields,
                  [fieldKey]: value,
                },
              }));
            }}
            style={{ width: "100%" }}
            status={errorMessage ? "error" : ""}
            placeholder={field.fieldName}
            disabled={portalSubmission}
          >
            {options?.map((option: any) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          {errorMessage && (
            <div style={{ color: "red", fontSize: "12px" }}>{errorMessage}</div>
          )}
        </>
      );
    }

    if (field.isDate) {
      // Date field
      return (
        <>
          <DatePicker
            format={"DD-MM-YYYY HH:mm:ss"}
            value={
              formData.dynamicFields?.[fieldKey]
                ? dayjs(formData.dynamicFields[fieldKey], "DD-MM-YYYY HH:mm:ss")
                : undefined
            }
            onChange={(date) => {
              setFormData((prev: any) => ({
                ...prev,
                dynamicFields: {
                  ...prev.dynamicFields,
                  [fieldKey]: date
                    ? dayjs(date).format("DD-MM-YYYY HH:mm:ss")
                    : null,
                },
              }));
            }}
            style={{ width: "100%" }}
            status={errorMessage ? "error" : ""}
            placeholder={field.fieldName}
            disabled={portalSubmission}
          />
          {errorMessage && (
            <div style={{ color: "red", fontSize: "12px" }}>{errorMessage}</div>
          )}
        </>
      );
    }

    if (field.isPredefined && field.predefinedValue) {
      // Predefined value field (read-only)
      return (
        <Input
          value={field.predefinedValue}
          disabled
          placeholder={field.fieldName}
        />
      );
    }

    // Default to standard input
    return (
      <>
        <Input
          value={formData.dynamicFields?.[fieldKey] || ""}
          onChange={(e) => {
            if (!validateInputOnlyNumeric(e.target.value)) {
              setErrors((prev: any) => ({
                ...prev,
                dynamicFields: {
                  ...(prev.dynamicFields || {}),
                  [fieldKey]: "Only Numbers are allowed",
                },
              }));

              return;
            } else {
              setErrors((prev: any) => ({
                ...prev,
                dynamicFields: {
                  ...(prev.dynamicFields || {}),
                  [fieldKey]: "",
                },
              }));
            }
            setFormData((prev: any) => ({
              ...prev,
              dynamicFields: {
                ...prev.dynamicFields,
                [fieldKey]: e.target.value,
              },
            }));
          }}
          disabled={portalSubmission}
          status={errorMessage ? "error" : ""}
          placeholder={field.fieldName}
        />
        {errorMessage && (
          <div style={{ color: "red", fontSize: "12px" }}>{errorMessage}</div>
        )}
      </>
    );
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrls: any
  ) => {
    for (const doc of mandatoryDocuments) {
      if (doc.required) {
        const isUploaded = documentUrls?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  const validateStep = (): boolean => {
    const newErrors: any = {};

    // if (!formData.memberId) newErrors.memberId = "Member ID is required";
    // if (!formData.patientOpdIpdNumber)
    //   newErrors.patientOpdIpdNumber = "Patient IPD/OPD No is required";

    // if (
    //   patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName
    //     ?.toUpperCase()
    //     ?.includes("Aditya birla"?.toUpperCase())
    // ) {
    //   if (!formData.panCardNumber) {
    //     newErrors.panCardNumber = "Pan Card Number is Required";
    //   }
    // }

    if (!formData.proposedLineOfTreatment)
      newErrors.proposedLineOfTreatment = "Line of Treatment is required";
    if (!formData.treatment) newErrors.treatment = "Treatment is required";

    if (!Array.isArray(formData.diagnosis) || formData.diagnosis.length === 0)
      newErrors.diagnosis = "At least one Diagnosis is required";

    if (!Array.isArray(formData.procedure) || formData.procedure.length === 0)
      newErrors.procedure = "At least one Procedure is required";

    if (!formData.roomType) newErrors.roomType = "Room Type is required";

    if (
      formData?.roomType?.toLowerCase() === "others" &&
      !formData.otherRoomType
    ) {
      newErrors.otherRoomType = "General Room Type is required";
    }

    if (!formData.dod) newErrors.dod = "Expected Date of Discharge is required";

    if (currentTreatmentFields?.length > 0) {
      currentTreatmentFields?.forEach((field: any) => {
        const fieldKey = field.fieldName
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");

        // Skip validation if the field is predefined
        if (field.isPredefined && field.predefinedValue) {
          return;
        }

        if (!formData.dynamicFields || !formData.dynamicFields[fieldKey]) {
          if (!newErrors.dynamicFields) {
            newErrors.dynamicFields = {};
          }
          newErrors.dynamicFields[fieldKey] = `${field.fieldName} is required`;
        }

        if (
          fieldKey === "number_of_dialysis_sessions_claimed" &&
          formData.dynamicFields[fieldKey] &&
          !validateInputOnlyNumeric(formData.dynamicFields[fieldKey])
        ) {
          if (!newErrors.dynamicFields) {
            newErrors.dynamicFields = {};
          }
          newErrors.dynamicFields[fieldKey] = `Only Numbers are allowed`;
        }
      });
    }

    if (
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName
        ?.toUpperCase()
        ?.includes("HDFC ERGO General Insurance".toUpperCase())
    ) {
      if (!formData.specialization) {
        newErrors.specialization = "Specialization is Required";
      }
    }

    if (!formData.roomRentAndNursingCharges)
      newErrors.roomRentAndNursingCharges =
        "Room Rent & Nursing Charges is required";
    if (!formData.otherHospitalExpense)
      newErrors.otherHospitalExpense = "Other Hospital Expenses is required";
    if (
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName
        ?.toUpperCase()
        ?.includes("HDFC ERGO General Insurance"?.toUpperCase())
    ) {
      if (!formData.totalRent) {
        newErrors.totalRent = "Total Rent is Required";
      }
    }

    setErrors(newErrors);
    return (
      Object.keys(newErrors).length === 0 &&
      (!newErrors.dynamicFields ||
        Object.keys(newErrors.dynamicFields).length === 0)
    );
  };

  const handleCancelPreview = () => {
    setPreviewDocument(false);
  };

  // Calculate total bill amount
  const calculateTotal = (): string => {
    const values = [
      formData.roomRentAndNursingCharges,
      formData.consultation,
      formData.otCharges,
      formData.surgeonAndAnesthetist,
      formData.investigation,
      formData.medicineAndConsumables,
      formData.otherHospitalExpense,
      formData.package,
    ];

    const total = values.reduce((sum, value) => {
      const num = parseFloat(value) || 0;
      return sum + num;
    }, 0);

    return total.toFixed(2);
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setSelectedDocument(null);
  };

  // Handle form submission
  const handleSubmit = async (isDraft: boolean = false): Promise<void> => {
    if (patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal) {
      try {
        let payload: any = {};

        if (auth?.role?.role !== "FO_STAFF") {
          if (!validateStep()) {
            message.error("Please fill all mandatory fields");
            return;
          }

          if (
            !validateRequiredDocuments(
              documentRequestResponseDocuments["requestedDocuments"],
              documentUrls
            )
          ) {
            errorMessage("Please, Upload all required documents");
            setConfirmationModal(false);
            return;
          }
        }

        const dynamicFieldsPayload = currentTreatmentFields?.reduce(
          (acc: any, field: any) => {
            const fieldKey = field.fieldName
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[^a-z0-9_]/g, "");

            const fieldValue = formData.dynamicFields?.[fieldKey];
            if (fieldValue !== undefined) {
              acc[field.fieldName.toLowerCase()] = fieldValue;
            }
            return acc;
          },
          {} as Record<string, any>
        );

        // const dynamicFieldsPayload = currentTreatmentFields.reduce(
        //   (acc: any, field: any) => {
        //     const fieldKey = field.fieldName
        //       .toLowerCase()
        //       .replace(/\s+/g, "_")
        //       .replace(/[^a-z0-9_]/g, "");

        //     let fieldValue = formData.dynamicFields?.[fieldKey];
        //     // Ensure date fields are formatted correctly
        //     if (field.isDate && fieldValue) {
        //       fieldValue = dayjs(fieldValue).format("DD-MM-YYYY HH:mm:ss");
        //     }
        //     if (fieldValue !== undefined) {
        //       acc[field.fieldName.toLowerCase()] = fieldValue;
        //     }
        //     return acc;
        //   },
        //   {} as Record<string, any>
        // );

        if (preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id && !isDraft) {
          payload = {
            casePreAuthId: preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id,
            claimOnPortal: true,
            hospitalId:
              patients?.fetchBasicDetails?.casesResponse?.healthCareId,
            schemeMetaDataId: schemeMetaDataId,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
            dod: formData.dod,
            total: calculateTotal(),
            totalAmount: calculateTotal(),
            casePreAuthIhxRequest: {
              claimOnPortal: true,
              casePreAuthRequest: {
                patientOpdIpdNumber: formData.patientOpdIpdNumber,
                beneficiaryId: formData.memberId,
                total: calculateTotal(),
                totalAmount: calculateTotal(),
              },
              documentURLs: documentUrls,
              isCovidCase: formData.isCovidCase === "yes" ? "true" : "false",
              proposedLineOfTreatment: formData.proposedLineOfTreatment,
              ...dynamicFieldsPayload,
              treatment: {
                id: formData.treatmentId,
                name: formData.treatmentName,
              },
              diagnosis:
                formData.diagnosisIds?.map((id: any) => {
                  const diagnosisItem = diagnosisOptions.find(
                    (option: any) => option.value == id
                  );
                  return {
                    id: id,
                    name: diagnosisItem?.label || "",
                  };
                }) || [],
              roomType: {
                id: formData.roomId,
                name: formData.roomType,
              },
              otherRoomType: {
                id: formData.otherRoomId,
                name: formData.otherRoomType,
              },
              procedure:
                formData.procedureIds?.map((id: any) => {
                  const procedureItem = procedureOptions.find(
                    (option: any) => option.value == id
                  );
                  return {
                    id: id,
                    name: procedureItem?.label || "",
                  };
                }) || [],
              packages: formData.package,
              panCardNumber: formData.panCardNumber,
              consultation: formData.consultation,
              otCharge: formData.otCharges,
              surgeonAndAnesthetist: formData.surgeonAndAnesthetist,
              investigation: formData.investigation,
              medicineAndConsumables: formData.medicineAndConsumables,
              otherHospitalExpense: formData.otherHospitalExpense,
              roomRentAndNursingCharges: formData.roomRentAndNursingCharges,
              dod: formData.dod,
              remarks: formData.remarks,
              hospitalId:
                patients?.fetchBasicDetails?.casesResponse?.healthCareId,
              schemeId:
                patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            },
            casePreAuthCghsRequest: null,
            casePreAuthVidalRequest: null,
            casePreAuthStarhealthRequest: null,
            casePreAuthEchsRequest: null,
          };

          if (caseId && visitId) {
            try {
              await fetchPreAuthIhxToPortal(caseId, visitId, payload);
              message.success("Successfully submitted to portal");
              setConfirmationModal(false);
              setPortalSubmission(true);
              fetchPreAuthData();
              fetchDocuments();
              await getTimelineDetail();
              await fetchStepperForStatus();
              dispatch(
                getBasicDetails({
                  id: visitId,
                })
              );
            } catch (error: any) {
              console.error("Error submitting to portal:", error);
              message.error(error?.message || "Failed to submit to portal");
              return;
            }
          }
        } else {
          payload = {
            schemeMetaDataId: schemeMetaDataId,
            claimOnPortal: true,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            total: calculateTotal(),
            totalAmount: calculateTotal(),
            casePreAuthId:
              preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id || 0,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
            dod: formData.dod,
            casePreAuthIhxRequest: {
              claimOnPortal: true,
              casePreAuthRequest: {
                patientOpdIpdNumber: formData.patientOpdIpdNumber,
                beneficiaryId: formData.memberId,
                total: calculateTotal(),
                totalAmount: calculateTotal(),
              },
              documentURLs: documentUrls,
              isCovidCase: formData.isCovidCase === "yes" ? "true" : "false",
              proposedLineOfTreatment: formData.proposedLineOfTreatment,
              ...dynamicFieldsPayload,
              treatment: {
                id: formData.treatmentId,
                name: formData.treatmentName,
              },
              diagnosis:
                formData.diagnosisIds?.map((id: any) => {
                  const diagnosisItem = diagnosisOptions.find(
                    (option: any) => option.value == id
                  );
                  return {
                    id: id,
                    name: diagnosisItem?.label || "",
                  };
                }) || [],
              roomType: {
                id: formData.roomId,
                name: formData.roomType,
              },
              otherRoomType: {
                id: formData.otherRoomId,
                name: formData.otherRoomType,
              },
              procedure:
                formData.procedureIds?.map((id: any) => {
                  const procedureItem = procedureOptions.find(
                    (option: any) => option.value == id
                  );
                  return {
                    id: id,
                    name: procedureItem?.label || "",
                  };
                }) || [],
              packages: formData.package,
              panCardNumber: formData.panCardNumber,
              consultation: formData.consultation,
              otCharge: formData.otCharges,
              surgeonAndAnesthetist: formData.surgeonAndAnesthetist,
              investigation: formData.investigation,
              medicineAndConsumables: formData.medicineAndConsumables,
              otherHospitalExpense: formData.otherHospitalExpense,
              roomRentAndNursingCharges: formData.roomRentAndNursingCharges,
              remarks: formData.remarks,
              dod: formData.dod,
              hospitalId:
                patients?.fetchBasicDetails?.casesResponse?.healthCareId,
              schemeId:
                patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            },
            casePreAuthCghsRequest: null,
            casePreAuthVidalRequest: null,
            casePreAuthStarhealthRequest: null,
            casePreAuthEchsRequest: null,
          };

          try {
            if (isDraft) {
              setIsSaving(true);
              if (preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id) {
                const res = await dispatch(
                  updatePreAuthData({
                    caseId: caseId,
                    visitId: visitId,
                    casePreAuthId:
                      preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id,
                    payload: payload,
                  })
                );
                setIsSaving(false);
                if (updatePreAuthData.fulfilled.match(res)) {
                  message.success("PreAuth Updated successfully");
                } else if (updatePreAuthData.rejected.match(res)) {
                  message.error("Please, Try again later");
                }
              } else {
                const res = await dispatch(
                  postPreAuthData({
                    caseId: caseId,
                    visitId: visitId,
                    payload: payload,
                  })
                );
                setIsSaving(false);
                if (postPreAuthData.fulfilled.match(res)) {
                  message.success("PreAuth Saved successfully");
                } else if (postPreAuthData.rejected.match(res)) {
                  message.error("Please, Try again later");
                }
              }
              setConfirmationModal(false);
              await fetchPreAuthData();
              await fetchDocuments();
              setUpdateButton(true);
              await fetchStepperForStatus();
              await getTimelineDetail();
              if (visitId) {
                dispatch(
                  getBasicDetails({
                    id: visitId,
                  })
                );
              }
            } else {
              if (caseId && visitId) {
                try {
                  setIsSubmitting(true);
                  await fetchPreAuthIhxToPortal(caseId, visitId, payload);
                  message.success("Successfully submitted to portal");
                  setConfirmationModal(false);
                  setPortalSubmission(true);
                  fetchPreAuthData();
                  fetchDocuments();
                  setIsSubmitting(false);
                  fetchStepperForStatus();
                  getTimelineDetail();
                  handleGetData();
                  if (visitId) {
                    dispatch(
                      getBasicDetails({
                        id: visitId,
                      })
                    );
                  }
                } catch (error: any) {
                  setIsSaving(false);
                  setIsSubmitting(false);
                  console.error("Error submitting to portal:", error);
                  message.error(error?.message || "Failed to submit to portal");
                  return;
                }
              }
            }
          } catch (error: any) {
            setIsSaving(false);
            setIsSubmitting(false);
            console.error("Error submitting pre-auth data:", error);
            message.error(error?.message || "Failed to submit pre-auth data");
          }
        }
      } catch (error: any) {
        setIsSaving(false);
        setIsSubmitting(false);
        console.error("Unexpected error in handleSubmit:", error);
        message.error("An unexpected error occurred while submitting.");
      }
    } else {
      if (isDraft) {
        try {
          let payload: any = {};

          if (auth?.role?.role !== "FO_STAFF") {
            if (!validateStep()) {
              message.error("Please fill all mandatory fields");
              return;
            }

            if (
              !validateRequiredDocuments(
                documentRequestResponseDocuments["requestedDocuments"],
                documentUrls
              )
            ) {
              errorMessage("Please, Upload all required documents");
              setConfirmationModal(false);
              return;
            }
          }

          const dynamicFieldsPayload = currentTreatmentFields?.reduce(
            (acc: any, field: any) => {
              const fieldKey = field.fieldName
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, "");

              const fieldValue = formData.dynamicFields?.[fieldKey];
              if (fieldValue !== undefined) {
                acc[field.fieldName.toLowerCase()] = fieldValue;
              }
              return acc;
            },
            {} as Record<string, any>
          );

          payload = {
            schemeMetaDataId: schemeMetaDataId,
            claimOnPortal: true,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            total: calculateTotal(),
            totalAmount: calculateTotal(),
            casePreAuthId:
              preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id || 0,
            integratedWithPortal:
              patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
            dod: formData.dod,
            casePreAuthIhxRequest: {
              claimOnPortal: true,
              casePreAuthRequest: {
                patientOpdIpdNumber: formData.patientOpdIpdNumber,
                beneficiaryId: formData.memberId,
                total: calculateTotal(),
                totalAmount: calculateTotal(),
              },
              documentURLs: documentUrls,
              isCovidCase: formData.isCovidCase === "yes" ? "true" : "false",
              proposedLineOfTreatment: formData.proposedLineOfTreatment,
              ...dynamicFieldsPayload,
              treatment: {
                id: formData.treatmentId,
                name: formData.treatmentName,
              },
              diagnosis:
                formData.diagnosisIds?.map((id: any) => {
                  const diagnosisItem = diagnosisOptions.find(
                    (option: any) => option.value == id
                  );
                  return {
                    id: id,
                    name: diagnosisItem?.label || "",
                  };
                }) || [],
              roomType: {
                id: formData.roomId,
                name: formData.roomType,
              },
              otherRoomType: {
                id: formData.otherRoomId,
                name: formData.otherRoomType,
              },
              procedure:
                formData.procedureIds?.map((id: any) => {
                  const procedureItem = procedureOptions.find(
                    (option: any) => option.value == id
                  );
                  return {
                    id: id,
                    name: procedureItem?.label || "",
                  };
                }) || [],
              packages: formData.package,
              panCardNumber: formData.panCardNumber,
              consultation: formData.consultation,
              otCharge: formData.otCharges,
              surgeonAndAnesthetist: formData.surgeonAndAnesthetist,
              investigation: formData.investigation,
              medicineAndConsumables: formData.medicineAndConsumables,
              otherHospitalExpense: formData.otherHospitalExpense,
              roomRentAndNursingCharges: formData.roomRentAndNursingCharges,
              remarks: formData.remarks,
              dod: formData.dod,
              hospitalId:
                patients?.fetchBasicDetails?.casesResponse?.healthCareId,
              schemeId:
                patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
            },
            casePreAuthCghsRequest: null,
            casePreAuthVidalRequest: null,
            casePreAuthStarhealthRequest: null,
            casePreAuthEchsRequest: null,
          };

          try {
            setIsSaving(true);
            if (preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id) {
              const res = await dispatch(
                updatePreAuthData({
                  caseId: caseId,
                  visitId: visitId,
                  casePreAuthId:
                    preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id,
                  payload: payload,
                })
              );
              setIsSaving(false);
              if (updatePreAuthData.fulfilled.match(res)) {
                message.success("PreAuth Updated successfully");
              } else if (updatePreAuthData.rejected.match(res)) {
                message.error("Please, Try again later");
              }
            } else {
              const res = await dispatch(
                postPreAuthData({
                  caseId: caseId,
                  visitId: visitId,
                  payload: payload,
                })
              );
              setIsSaving(false);
              if (postPreAuthData.fulfilled.match(res)) {
                message.success("PreAuth Saved successfully");
              } else if (postPreAuthData.rejected.match(res)) {
                message.error("Please, Try again later");
              }
            }
            setConfirmationModal(false);
            await fetchPreAuthData();
            await fetchDocuments();
            setUpdateButton(true);
            await fetchStepperForStatus();
            await getTimelineDetail();
            if (visitId) {
              dispatch(
                getBasicDetails({
                  id: visitId,
                })
              );
            }
          } catch (error: any) {
            setIsSaving(false);
            setIsSubmitting(false);
            console.error("Error submitting pre-auth data:", error);
            message.error(error?.message || "Failed to submit pre-auth data");
          }
        } catch (error: any) {
          setIsSaving(false);
          setIsSubmitting(false);
          console.error("Unexpected error in handleSubmit:", error);
          message.error("An unexpected error occurred while submitting.");
        }
      } else {
        setManuallyClaimIdPortal(true);
      }
    }
  };

  const handleSaveIhx = async () => {
    // if (!validateStep()) return;

    if (
      !validateRequiredDocuments(
        documentRequestResponseDocuments["requestedDocuments"],
        documentUrls
      )
    ) {
      errorMessage("Please, Upload all required documents");
      setConfirmationModal(false);
      return;
    }
    let payload: any = {};
    const actionType = "intimate";
    const dynamicFieldsPayload = currentTreatmentFields.reduce(
      (acc: any, field: any) => {
        const fieldKey = field.fieldName
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");

        const fieldValue = formData.dynamicFields?.[fieldKey];
        if (fieldValue !== undefined) {
          acc[field.fieldName.toLowerCase()] = fieldValue;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    // const dynamicFieldsPayload = currentTreatmentFields.reduce(
    //   (acc: any, field: any) => {
    //     const fieldKey = field.fieldName
    //       .toLowerCase()
    //       .replace(/\s+/g, "_")
    //       .replace(/[^a-z0-9_]/g, "");

    //     let fieldValue = formData.dynamicFields?.[fieldKey];
    //     // Ensure date fields are formatted correctly
    //     if (field.isDate && fieldValue) {
    //       fieldValue = dayjs(fieldValue).format("DD-MM-YYYY HH:mm:ss");
    //     }
    //     if (fieldValue !== undefined) {
    //       acc[field.fieldName.toLowerCase()] = fieldValue;
    //     }
    //     return acc;
    //   },
    //   {} as Record<string, any>
    // );

    payload = {
      action: actionType,
      schemeMetaDataId: schemeMetaDataId,
      portalMetaDataId:
        patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
      total: calculateTotal(),
      totalAmount: calculateTotal(),
      casePreAuthId: preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id
        ? preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id
        : 0,
      dod: formData.dod,
      casePreAuthIhxRequest: {
        action: actionType,
        casePreAuthRequest: {
          patientOpdIpdNumber: formData.patientOpdIpdNumber,
          beneficiaryId: formData.memberId,
          total: calculateTotal(),
          totalAmount: calculateTotal(),
        },
        documentURLs: documentUrls,
        isCovidCase: formData.isCovidCase === "yes" ? "true" : "false",
        proposedLineOfTreatment: formData.proposedLineOfTreatment,
        ...dynamicFieldsPayload,
        treatment: {
          id: formData.treatmentId,
          name: formData.treatmentName,
        },
        diagnosis:
          formData.diagnosisIds?.map((id: any) => {
            const diagnosisItem = diagnosisOptions.find(
              (option: any) => option.value == id
            );
            return {
              id: id,
              name: diagnosisItem?.label || "",
            };
          }) || [],
        roomType: {
          id: formData.roomId,
          name: formData.roomType,
        },
        otherRoomType: {
          id: formData.otherRoomId,
          name: formData.otherRoomType,
        },
        procedure:
          formData.procedureIds?.map((id: any) => {
            const procedureItem = procedureOptions.find(
              (option: any) => option.value == id
            );
            return {
              id: id,
              name: procedureItem?.label || "",
            };
          }) || [],
        packages: formData.package,
        panCardNumber: formData.panCardNumber,
        consultation: formData.consultation,
        otCharge: formData.otCharges,
        surgeonAndAnesthetist: formData.surgeonAndAnesthetist,
        investigation: formData.investigation,
        medicineAndConsumables: formData.medicineAndConsumables,
        otherHospitalExpense: formData.otherHospitalExpense,
        roomRentAndNursingCharges: formData.roomRentAndNursingCharges,
        remarks: formData.remarks,
        dod: formData.dod,
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
      },
      casePreAuthCghsRequest: null,
      casePreAuthVidalRequest: null,
      casePreAuthStarhealthRequest: null,
      casePreAuthEchsRequest: null,
    };

    try {
      setIsIntimating(true);
      const res = await dispatch(
        postPreAuthData({
          caseId: caseId,
          visitId: visitId,
          payload: payload,
        })
      );
      setIsIntimating(false);

      if (postPreAuthData.fulfilled.match(res)) {
        message.success("PreAuth Sent to QC Successfully");
      } else if (postPreAuthData.rejected.match(res)) {
        message.error("Please, Try again later");
      }
      setConfirmationModal(false);
      await fetchPreAuthData();
      await fetchDocuments();
      await fetchStepperForStatus();
      await getTimelineDetail();
      if (visitId) {
        dispatch(
          getBasicDetails({
            id: visitId,
          })
        );
      }
    } catch (err) {
      message.error("An unexpected error occurred while submitting.");
      setIsIntimating(false);
    }
  };

  const [manualStatusUpdateCount, setManualStatusUpdateCount] =
    useState<number>(0);

  const {
    stepperData,
    currentStep,
    stageAllState,
    // schemePortalStatusList,
    portalData,
  } = useSelector((state: RootState) => state.preAuth);
  const patientStore = useSelector((state: RootState) => state.patients);
  const [loading, setLoading] = useState<boolean>(true);
  const [deficiencyModal, setDeficiencyModal] = useState(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [portalSubmission, setPortalSubmission] = useState(false);

  useEffect(() => {
    const documentList = [
      ...documentTypes
        .filter(
          (item: any) => item.documentList && item.documentList.length > 0
        )
        .flatMap((item: any, index: number) =>
          item.documentList!?.map((doc: any, docIndex: any) => ({
            key: `${index + 1}`,
            label: item.label, // Document Type
            documentUrl: doc.documentUrl, // URL for download
            id: doc.id,
            document: (
              <a
                href={doc.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {doc.documentName}
              </a>
            ),
            documentType: doc.documentType,
            documentName: doc.documentName,
            deficiencyCheckStatus:
              doc.deficiencyCheckStatus === true
                ? true
                : doc.deficiencyCheckStatus === false
                ? false
                : null,
            deficiency:
              doc.deficiencyCheckStatus === true
                ? true
                : doc.deficiencyCheckStatus === false
                ? false
                : null,
            remarks: doc.remarks || null,
            documentLabelType: doc.documentLabelType,
            actions: item,
          }))
        ),
    ];

    setDocumentUrls(documentList);
  }, [documentTypes]);

  useEffect(() => {
    if (preAuthData && preAuthData.casePreAuthIhx) {
      if (preAuthData.casePreAuthIhx.id !== null) {
        setUpdateButton(true);
      }
      // Extract values from nested objects
      const ihxData = preAuthData.casePreAuthIhx;

      // Extract values from strings with format "label:id"
      const extractIdValue = (str: any) => {
        if (!str) return "";
        const parts = str.split(":");
        return parts.length > 1 ? parts[1] : str;
      };

      // Extract label without ID
      const extractLabel = (str: any) => {
        if (!str) return "";
        return str.split(":")?.[0] || "";
      };

      // Parse proposedLineOfTreatment and treatment
      const proposedLineOfTreatment = ihxData.proposedLineOfTreatment || "";
      const proposedLineOfTreatmentId =
        ihxData.proposedLineOfTreatment?.split("::")[1] || "";
      const treatment = ihxData.treatment?.split(":")?.[0] || "";
      const treatmentId = ihxData.treatmentId;

      // Parse room type
      const roomType = ihxData.roomType?.split(":")?.[0] || "";
      const roomId = ihxData.roomTypeId || "";

      const otherRoomType = ihxData.otherRoomType?.split(":")?.[0] || "";
      const otherRoomId = ihxData.otherRoomId || "";

      // Parse diagnosis and procedure to remove the :id parts
      const diagnosisLabels = ihxData.diagnosis
        ? ihxData.diagnosis?.map((item: any) => extractLabel(item))
        : [];
      const procedureLabels = ihxData.procedure
        ? ihxData.procedure?.map((item: any) => extractLabel(item))
        : [];

      const dynamicFields: any = {};

      // Loop through all properties of ihxData that might be dynamic fields

      setFormData({
        // Step 1 - Member Details
        memberId: preAuthData.beneficiaryId || "",
        patientOpdIpdNumber: preAuthData.patientOpdIpdNumber || "",
        policyNo: preAuthData.policyNo || "",

        // Step 2 - Treatment Details
        isCovidCase: ihxData.isCovidCase ? "yes" : "no",
        proposedLineOfTreatment,
        treatment,
        diagnosis: diagnosisLabels,
        diagnosisIds: ihxData.diagnosisIds || [],
        procedure: procedureLabels,
        procedureIds: ihxData.procedureIds || [],
        roomType,
        otherRoomType,
        otherRoomId,
        roomId,
        treatmentName: treatment,
        treatmentId: treatmentId,
        dod:
          ihxData?.casePreAuthResponse?.dod &&
          dayjs(
            ihxData?.casePreAuthResponse?.dod,
            "DD-MM-YYYY HH:mm:ss",
            true
          ).isValid()
            ? dayjs(
                ihxData?.casePreAuthResponse?.dod,
                "DD-MM-YYYY HH:mm:ss"
              ).format("DD-MM-YYYY HH:mm:ss")
            : null,

        // Step 3 - Bill Details
        package: ihxData.packages || "",
        panCardNumber: ihxData.panCardNumber || "",
        roomRentAndNursingCharges: ihxData.roomRentAndNursingCharges || "",
        consultation: ihxData.consultation || "",
        otCharges: ihxData.otCharge || "",
        surgeonAndAnesthetist: ihxData.surgeonAndAnesthetist || "",
        investigation: ihxData.investigation || "",
        medicineAndConsumables: ihxData.medicineAndConsumables || "",
        otherHospitalExpense: ihxData.otherHospitalExpense || "",

        // Step 4 - Document Upload
        documents: ihxData.documentsList || [],
        remarks: ihxData.remarks || "",
      });
      if (
        preAuthData?.casePreAuthIhx?.casePreAuthResponse?.submitted ||
        preAuthData?.casePreAuthIhx?.claimId
      ) {
        setPortalSubmission(true);
      }
      // setDocumentUrls(ihxData.documentsList)
    }
  }, [preAuthData]);

  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      dynamicFields: {},
    }));
  }, [formData.treatmentId]);

  useEffect(() => {
    if (currentTreatmentFields?.length && preAuthData?.casePreAuthIhx) {
      const updatedDynamicFields = currentTreatmentFields?.reduce(
        (acc: any, field: any) => {
          let fieldName = field.fieldName.toLowerCase(); // Normalize for comparison
          let apiKey = Object.keys(preAuthData.casePreAuthIhx)?.find(
            (key) => key.toLowerCase() === fieldName
          );

          if (apiKey && preAuthData.casePreAuthIhx[apiKey]) {
            const fieldKey = apiKey
              .toLowerCase()
              .replace(/\s+/g, "_")
              .replace(/[^a-z0-9_]/g, "");
            // acc[fieldKey] = preAuthData.casePreAuthIhx[apiKey]; // Store matching values in an object
            acc[fieldKey] = apiKey?.toLowerCase()?.includes("date")
              ? preAuthData.casePreAuthIhx[apiKey] &&
                dayjs(
                  preAuthData.casePreAuthIhx[apiKey],
                  "DD-MM-YYYY HH:mm:ss",
                  true
                ).isValid()
                ? dayjs(
                    preAuthData.casePreAuthIhx[apiKey],
                    "DD-MM-YYYY HH:mm:ss"
                  ).format("DD-MM-YYYY HH:mm:ss")
                : null
              : preAuthData.casePreAuthIhx[apiKey];
          }

          return acc;
        },
        {}
      );

      // Update formData in a single state update
      if (
        Object.keys(updatedDynamicFields).length > 0 &&
        parseInt(formData.treatmentId) ===
          preAuthData?.casePreAuthIhx.treatmentId
      ) {
        setFormData((prev: any) => ({
          ...prev,
          dynamicFields: {
            ...prev.dynamicFields,
            ...updatedDynamicFields,
          },
        }));
      }
    }
  }, [currentTreatmentFields, formData.treatmentId, preAuthData]);

  const currentStageDetails = stepperData?.currentVisitStage;
  //   const currentActiveStage = stepperData?.visitStages[currentStep];
  const currentActiveStage = preAuth?.stepperData?.visitStages?.find(
    (stage: any) =>
      stage?.visitStageId ===
      (typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]))
  );

  const customStatus =
    currentActiveStage?.step && currentStageDetails?.step
      ? currentActiveStage.step === currentStageDetails.step
        ? currentStageDetails.stateId
        : currentActiveStage.step > currentStageDetails.step
        ? 8
        : 4
      : null;

  const schemeMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

  const fetchDocuments = async () => {
    if (schemeMetaDataId && caseId && visitId) {
      try {
        setLoading(true);
        const AllDocumentList = await getAllStarhealthDocumentsData(
          caseId,
          visitId,
          schemeMetaDataId
        );
        setDocumentTypes(AllDocumentList?.["PreAuth"]);
      } catch (err) {
        errorMessage("Please, Try again after sometime");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [schemeMetaDataId]);

  useEffect(() => {
    const listOfUploadedDocuments = documentTypes?.flatMap(
      (item: any) =>
        item?.documentList?.map((doc: any) => ({
          id: doc?.id,
          documentName: doc?.documentName,
          documentType: doc?.documentType,
          documentUrl: doc?.documentUrl,
          documentLabelType: doc?.documentLabelType,
          deficiencyCheckStatus: doc?.deficiencyCheckStatus,
        })) || []
    );

    setDocumentUrls(listOfUploadedDocuments);
  }, [documentTypes]);

  useEffect(() => {
    if (currentActiveStage?.visitStageId) {
      dispatch(
        getVisitStageStates({ visitStageId: currentActiveStage?.visitStageId })
      );
    }
  }, [currentActiveStage?.visitStageId]);

  const fetchDeficiencyData = async (
    selectedDocument: any,
    selectedDocumentId: any
  ) => {
    try {
      if (selectedDocumentId) {
        // dispatch(clearDeficiencyCheck());
        // const firstRes = await dispatch(
        //   getDeficiencyQuestions(selectedDocumentId)
        // );
        setFetchingDeficiencyData(true);
        const secondRes = await dispatch(
          getDeficiencyQuestionData(selectedDocumentId)
        );
        setFetchingDeficiencyData(false);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
      setFetchingDeficiencyData(false);
    }
  };

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: any
  ) => {
    await fetchDeficiencyData(document, document?.id);
    setPreviewDocumentUrl(documentUrl);
    // setDeficiencyModal(true);
    setSelectedDocument(document);
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number
  ) => {
    const combinedUrls: any = [...documentUrls];
    const deleteDocument = combinedUrls?.find(
      (doc: any, index: any) => doc?.documentUrl === documentUrl
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    const newUpdatedDocumentList = documentUrls?.filter(
      (doc: any, index: any) => doc?.documentUrl !== documentUrl
    );
    setDocumentUrls(newUpdatedDocumentList);

    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
      } catch (err) {
        console.error(err);
        message.error("Please, Try again later");
      }
      // fetchDocuments();
    }
  };

  const formattedDataSource = [
    // Process newly uploaded documents from documentUrls state
    ...documentUrls?.map((doc, index) => ({
      key: `${index + 1}`, // Unique key for new uploads
      label: doc.documentLabelType || "New Upload", // Use documentLabelType if available
      documentUrl: doc.documentUrl, // URL for download
      documentType: doc.documentType,
      id: doc.id,
      document: (
        <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer">
          {doc.documentName}
        </a>
      ),
      deficiency:
        doc.deficiencyCheckStatus === true
          ? true
          : doc.deficiencyCheckStatus === false
          ? false
          : null,
      documentName: doc.documentName,
      deficiencyCheckStatus:
        doc.deficiencyCheckStatus === true
          ? true
          : doc.deficiencyCheckStatus === false
          ? false
          : null,
      documentLabelType: doc.documentLabelType || null,
      remarks: doc.remarks || "N/A",
      actions: {}, // Empty object for now
    })),
  ];

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleSetDocumentUrl = (url: string) => {
    setDocumentUrl(url);
  };

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      await downloadDocument(fileName, url);
      message.success("Document Downloaded successfully");
    } catch (error) {
      message.error("Download failed");
    }
  };

  const [isApproved, setIsApproved] = useState(false);
  const [isApprovedInitiated, setIsApprovedInitiated] = useState(false);

  useEffect(() => {
    setIsApproved(preAuthData?.casePreAuthIhx?.casePreAuthResponse?.approved);
  }, [preAuthData?.casePreAuthIhx?.casePreAuthResponse?.approved]);

  const handleApprovePreAuth = async () => {
    try {
      if (!validateStep()) {
        message.error("Please fill all mandatory fields");
        return;
      }
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls
        )
      ) {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        return;
      }
      setIsApprovedInitiated(true);
      const dynamicFieldsPayload = currentTreatmentFields?.reduce(
        (acc: any, field: any) => {
          const fieldKey = field.fieldName
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");

          const fieldValue = formData.dynamicFields?.[fieldKey];
          if (fieldValue !== undefined) {
            acc[field.fieldName.toLowerCase()] = fieldValue;
          }
          return acc;
        },
        {} as Record<string, any>
      );
      const updatedFormData = {
        caseId: patients?.fetchBasicDetails?.casesResponse?.id,
        visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        stage: "PreAuth",
        approved: true,
        casePreAuthId:
          preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id || 0,
        preAuthRequest: {
          schemeMetaDataId: schemeMetaDataId,
          portalMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          total: calculateTotal(),
          totalAmount: calculateTotal(),
          casePreAuthId:
            preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id || 0,
          dod: formData.dod,
          casePreAuthIhxRequest: {
            claimOnPortal: true,
            casePreAuthRequest: {
              patientOpdIpdNumber: formData.patientOpdIpdNumber,
              beneficiaryId: formData.memberId,
              total: calculateTotal(),
              totalAmount: calculateTotal(),
            },
            documentURLs: documentUrls,
            isCovidCase: formData.isCovidCase === "yes" ? "true" : "false",
            proposedLineOfTreatment: formData.proposedLineOfTreatment,
            ...dynamicFieldsPayload,
            treatment: {
              id: formData.treatmentId,
              name: formData.treatmentName,
            },
            diagnosis:
              formData.diagnosisIds?.map((id: any) => {
                const diagnosisItem = diagnosisOptions.find(
                  (option: any) => option.value == id
                );
                return {
                  id: id,
                  name: diagnosisItem?.label || "",
                };
              }) || [],
            roomType: {
              id: formData.roomId,
              name: formData.roomType,
            },
            otherRoomType: {
              id: formData.otherRoomId,
              name: formData.otherRoomType,
            },
            procedure:
              formData.procedureIds?.map((id: any) => {
                const procedureItem = procedureOptions.find(
                  (option: any) => option.value == id
                );
                return {
                  id: id,
                  name: procedureItem?.label || "",
                };
              }) || [],
            packages: formData.package,
            panCardNumber: formData.panCardNumber,
            consultation: formData.consultation,
            otCharge: formData.otCharges,
            surgeonAndAnesthetist: formData.surgeonAndAnesthetist,
            investigation: formData.investigation,
            medicineAndConsumables: formData.medicineAndConsumables,
            otherHospitalExpense: formData.otherHospitalExpense,
            roomRentAndNursingCharges: formData.roomRentAndNursingCharges,
            remarks: formData.remarks,
            dod: formData.dod,
            hospitalId:
              patients?.fetchBasicDetails?.casesResponse?.healthCareId,
            schemeId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          },
          casePreAuthCghsRequest: null,
          casePreAuthVidalRequest: null,
          casePreAuthStarhealthRequest: null,
          casePreAuthEchsRequest: null,
        },
      };
      const res = await submitApprovalIhx(updatedFormData);
      setIsApproved(true);
      setIsApprovedInitiated(false);
      setConfirmationModal(false);
      message.success("PreAuth Approved Successfully");
      await getTimelineDetail();
      await fetchStepperForStatus();
      fetchPreAuthData();
      fetchDocuments();
      if (visitId) {
        dispatch(
          getBasicDetails({
            id: visitId,
          })
        );
      }
    } catch (err) {
      errorMessage("Please, Try again later");
      setIsApprovedInitiated(false);
      setIsApproved(false);
    }
  };

  const handleGetData = async () => {
    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    if (caseId && visitId) {
      await dispatch(getPortalDataIhx({ caseId, visitId }));
    }
  };

  // useEffect(() => {
  //   if (
  //     preAuthData?.casePreAuthIhx?.casePreAuthResponse?.initiated &&
  //     !preAuthData?.casePreAuthIhx?.claimId
  //   ) {
  //     setTimeout(() => {
  //       fetchPreAuthData();
  //       handleGetData();
  //     }, 5000);

  //     if (!preAuthData?.casePreAuthIhx?.claimId) {
  //       setTimeout(() => {
  //         fetchPreAuthData();
  //         handleGetData();
  //       }, 60000);
  //     }
  //   }
  // }, [preAuthData]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let pollCount = 0;
    const maxPolls = 24;
    const modifiedAt = preAuth?.portalData?.modifiedAt;

    const modifiedTime = new Date(modifiedAt).getTime();
    const now = Date.now();
    const diffSeconds = (now - modifiedTime) / 1000;

    if (
      preAuth?.portalData?.casePreAuthIhx?.casePreAuthResponse?.initiated &&
      !preAuth?.portalData?.casePreAuthIhx?.claimId &&
      diffSeconds <= 78
    ) {
      intervalId = setInterval(() => {
        pollCount++;
        if (
          patients?.fetchBasicDetails?.casesResponse?.id &&
          patients?.fetchBasicDetails?.visitInfoResponse?.id
        ) {
          fetchPreAuthData();
          handleGetData();
        }

        if (preAuth?.portalData?.casePreAuthIhx?.claimId) {
          if (intervalId) clearInterval(intervalId);
          if (timeoutId) clearTimeout(timeoutId);
        }

        if (pollCount >= maxPolls) {
          if (intervalId) clearInterval(intervalId);
          // setShowManualClaimIdModal(true);
        }
      }, 5000);

      timeoutId = setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
        // setShowManualClaimIdModal(true);
      }, 120000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
    // Add dependencies as needed
  }, [
    preAuth?.portalData?.casePreAuthIhx?.claimId,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    preAuth?.portalData?.modifiedAt,
    preAuth?.portalData?.casePreAuthIhx?.casePreAuthResponse?.initiated,
    preAuth?.portalData?.casePreAuthIhx?.claimId,
  ]);

  const handleAttachDocuments = async (updatedDocumentUrls: any) => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    payloadSchema["casePreAuthAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema
        );
        message.success(`documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleSaveData = async () => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    const updatedDocumentUrls: any = documentUrls?.filter((document: any) =>
      documentRequestResponseDocuments["responseDocuments"]?.some(
        (doc: any) => doc?.label === document?.documentLabelType
      )
    );

    payloadSchema["casePreAuthAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema
        );
        message.success(`Response documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [confirmationState, setConfirmationState] = useState<string>("");

  const handleSaveConfirmationModal = (confirmationState: string) => {
    setConfirmationModal(true);
    setConfirmationState(confirmationState);

    if (confirmationState === "save") {
      setTitle("Do You want to save PreAuth ?");
    } else if (confirmationState === "update") {
      setTitle("Do You want to update PreAuth ?");
    } else if (confirmationState === "intimate") {
      setTitle("Do you want to send PreAuth for QC ?");
    } else if (confirmationState === "submit") {
      setTitle("Do You want to submit PreAuth ?");
    } else if (confirmationState === "approve") {
      setTitle("Do You want to approve PreAuth ?");
    }
  };

  const handleSave = async () => {
    if (confirmationState === "save") {
      await handleSubmit(true);
    } else if (confirmationState === "update") {
      await handleSubmit(true);
    } else if (confirmationState === "intimate") {
      await handleSaveIhx();
    } else if (confirmationState === "submit") {
      await handleSubmit(false);
    } else if (confirmationState === "approve") {
      await handleApprovePreAuth();
    }

    setConfirmationModal(false);
  };

  const handleSaveManually = async (values: any) => {
    try {
      if (auth?.role?.role !== "FO_STAFF") {
        if (!validateStep()) {
          message.error("Please fill all mandatory fields");
          return;
        }

        if (
          !validateRequiredDocuments(
            documentRequestResponseDocuments["requestedDocuments"],
            documentUrls
          )
        ) {
          errorMessage("Please, Upload all required documents");
          setConfirmationModal(false);
          return;
        }
      }

      let payload: any = {};
      const dynamicFieldsPayload = currentTreatmentFields.reduce(
        (acc: any, field: any) => {
          const fieldKey = field.fieldName
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");

          const fieldValue = formData.dynamicFields?.[fieldKey];
          if (fieldValue !== undefined) {
            acc[field.fieldName.toLowerCase()] = fieldValue;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      if (patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId) {
        payload = {
          schemeMetaDataId: schemeMetaDataId,
          portalMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          total: calculateTotal(),
          totalAmount: calculateTotal(),
          casePreAuthId: preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id
            ? preAuthData?.casePreAuthIhx?.casePreAuthResponse?.id
            : 0,
          dod: formData.dod,
          casePreAuthIhxRequest: {
            manualPreAuth: true,
            claimId: values?.manualId,
            casePreAuthRequest: {
              patientOpdIpdNumber: formData.patientOpdIpdNumber,
              beneficiaryId: formData.memberId,
              total: calculateTotal(),
              totalAmount: calculateTotal(),
            },
            documentURLs: documentUrls,
            isCovidCase: formData.isCovidCase === "yes" ? "true" : "false",
            proposedLineOfTreatment: formData.proposedLineOfTreatment,
            ...dynamicFieldsPayload,
            treatment: {
              id: formData.treatmentId,
              name: formData.treatmentName,
            },
            diagnosis:
              formData.diagnosisIds?.map((id: any) => {
                const diagnosisItem = diagnosisOptions.find(
                  (option: any) => option.value == id
                );
                return {
                  id: id,
                  name: diagnosisItem?.label || "",
                };
              }) || [],
            roomType: {
              id: formData.roomId,
              name: formData.roomType,
            },
            otherRoomType: {
              id: formData.otherRoomId,
              name: formData.otherRoomType,
            },
            procedure:
              formData.procedureIds?.map((id: any) => {
                const procedureItem = procedureOptions.find(
                  (option: any) => option.value == id
                );
                return {
                  id: id,
                  name: procedureItem?.label || "",
                };
              }) || [],
            packages: formData.package,
            panCardNumber: formData.panCardNumber,
            consultation: formData.consultation,
            otCharge: formData.otCharges,
            surgeonAndAnesthetist: formData.surgeonAndAnesthetist,
            investigation: formData.investigation,
            medicineAndConsumables: formData.medicineAndConsumables,
            otherHospitalExpense: formData.otherHospitalExpense,
            roomRentAndNursingCharges: formData.roomRentAndNursingCharges,
            remarks: formData.remarks,
            dod: formData.dod,
            hospitalId:
              patients?.fetchBasicDetails?.casesResponse?.healthCareId,
            schemeId:
              patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          },
          casePreAuthCghsRequest: null,
          casePreAuthVidalRequest: null,
          casePreAuthStarhealthRequest: null,
          casePreAuthEchsRequest: null,
          integratedWithPortal:
            patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
        };

        if (caseId && visitId) {
          try {
            setIsSubmitting(true);
            await fetchPreAuthIhxToPortal(caseId, visitId, payload);
            message.success("Successfully submitted to portal");
            setManuallyClaimIdPortal(false);
            setPortalSubmission(true);
            fetchPreAuthData();
            fetchDocuments();
            await getTimelineDetail();
            await fetchStepperForStatus();
            dispatch(
              getBasicDetails({
                id: visitId,
              })
            );
            setIsSubmitting(false);
          } catch (error: any) {
            console.error("Error submitting to portal:", error);
            message.error(error?.message || "Failed to submit to portal");
            setIsSubmitting(false);
            setManuallyClaimIdPortal(false);
          }
        }
      }
    } catch (err) {
      errorMessage("Please, Try again later");
      setIsSubmitting(false);
      setManuallyClaimIdPortal(false);
    }
  };

  return (
    <div style={{ width: "100%", overflow: "auto" }}>
      <TreatmentDetails
        formData={formData}
        handleInputChange={handleInputChange}
        errors={errors}
        portalSubmission={portalSubmission}
        diagnosisOptions={diagnosisOptions}
        procedureOptions={procedureOptions}
        roomTypeOptions={roomTypeOptions}
        treatmentDataOptions={treatmentDataOptions}
        patientStore={patientStore}
        generalRoomTypeOptions={generalRoomTypeOptions}
        currentTreatmentFields={currentTreatmentFields}
        renderTreatmentField={renderTreatmentField}
        proposedLineOfTreatment={proposedLineOfTreatment}
        isApproved={isApproved}
      />

      <BillDetails
        calculateTotal={calculateTotal}
        formData={formData}
        handleInputChange={handleInputChange}
        errors={errors}
        portalSubmission={portalSubmission}
        isApproved={isApproved}
      />
      {loading ? (
        <div
          style={{
            margin: "auto",
            width: "100%,",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        documentRequestResponseDocuments?.["requestedDocuments"]?.length >
          0 && (
          <DocumentUploadIhx
            documentTypes={documentTypes}
            documentUrls={documentUrls}
            handleFileChange={handleFileChange}
            handleOpen={handleOpen}
            portalSubmission={portalSubmission}
            documentRequestResponseDocuments={documentRequestResponseDocuments}
            isApproved={isApproved}
            uploadFileList={uploadFileList}
            isIntimated={
              preAuthData?.casePreAuthIhx?.casePreAuthResponse?.intimated
            }
          />
        )
      )}

      {documentRequestResponseDocuments?.["responseDocuments"]?.length > 0 && (
        <ResponseDocumentUploadIhx
          documentTypes={documentTypes}
          documentUrls={documentUrls}
          handleFileChange={handleFileChange}
          handleOpen={handleOpen}
          portalSubmission={portalSubmission}
          documentRequestResponseDocuments={documentRequestResponseDocuments}
          handleSaveData={handleSaveData}
          currentStage={preAuth?.stepperData?.currentVisitStage}
          activeKey={activeKey}
          uploadFileList={uploadFileList}
        />
      )}

      <div className="buttons" style={{ margin: "16px 0" }}>
        {!portalSubmission && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.8rem",
            }}
          >
            {!updateButton && (
              <div style={{ width: "16%" }}>
                <Button
                  type="primary"
                  style={{
                    backgroundColor: "rgb(0, 76, 143)",
                    color: "#fff",
                    boxShadow: "0px 2px 8px 0px #00000040",
                    width: "100%",
                  }}
                  // onClick={() => handleSubmit(true)}
                  onClick={() => handleSaveConfirmationModal("save")}
                  loading={isSaving}
                >
                  SAVE
                </Button>
              </div>
            )}
            {updateButton &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.initiated && (
                // !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.approved &&
                // !(
                //   auth?.role?.role == "FO_STAFF" &&
                //   preAuthData?.casePreAuthIhx?.casePreAuthResponse?.intimated
                // ) &&
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                      width: "100%",
                    }}
                    // onClick={() => handleSubmit(true)}
                    onClick={() => handleSaveConfirmationModal("update")}
                    loading={isSaving}
                  >
                    Update
                  </Button>
                </div>
              )}

            {hasFunctionality("casePreAuthIntimate") &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.intimated &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.initiated &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.approved && (
                <div style={{ width: "16%" }}>
                  <Button
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                      width: "100%",
                    }}
                    className="me-2"
                    // onClick={() => handleSaveIhx()}
                    onClick={() => handleSaveConfirmationModal("intimate")}
                    loading={isIntimating}
                  >
                    Send To QC
                  </Button>
                </div>
              )}

            {hasFunctionality("preAuthApprover") &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.initiated &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.approved &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.submitted && (
                <div style={{ width: "16%" }}>
                  <Button
                    loading={isApprovedInitiated}
                    className="me-2"
                    // onClick={handleApprovePreAuth}
                    onClick={() => handleSaveConfirmationModal("approve")}
                    disabled={isApproved}
                    // style={{ }}
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                      width: "100%",
                      opacity: !isApproved ? 1 : 0.5,
                      pointerEvents: !isApproved ? "auto" : "none",
                    }}
                    // style={{display: "flex", alignItems: "center", background: "#008B8B", border: "1px solid #008B8B", boxShadow: "0px 2px 8px 0px #00000040", borderRadius: "10px", padding: "12px 25px"}}
                  >
                    QC Approve
                  </Button>
                </div>
              )}
            {hasFunctionality("casePreAuthSubmission") &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.initiated &&
              !preAuthData?.casePreAuthIhx?.casePreAuthResponse?.submitted && (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                      width: "100%",
                    }}
                    // onClick={() => handleSubmit(false)}
                    onClick={() => handleSaveConfirmationModal("submit")}
                    loading={isSubmitting}
                  >
                    {patients?.fetchBasicDetails?.casesResponse
                      ?.integratedWithPortal
                      ? "Initiate"
                      : "Submit Manually"}
                  </Button>
                </div>
              )}
          </div>
        )}
      </div>

      {selectedFile?.file && !selectedFiles?.length && (
        <FilePreviewModal
          isVisible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          documentType={previewFile.documentType}
          handleUpload={handleUpload}
          showUploadButton={previewFile.showUpload}
          key={selectedFile?.file?.lastModified || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <PatientDetailsStageDocument
        // key={documentUrl}
        handleCancel={handleCancelPreview}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        // handleSetDocumentUrl={handleSetDocumentUrl}
        claimId={preAuthData?.casePreAuthIhx?.claimId}
        documents={documentUrls}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
        activeKey={activeKey}
        clickedActiveTab={clickedActiveTab}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
        isSubmittedStatus={portalSubmission}
        fetchingDeficiencyData={fetchingDeficiencyData}
      />
      <DocumentDeficiencyModal
        key={previewDocumentUrl}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        documentUrl={previewDocumentUrl}
        documentName={documentName}
        handleDownloadDocument={handleDownloadDocument}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
      />

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={title}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={
          isSaving || isIntimating || isApprovedInitiated || isSubmitting
        }
      />

      <ManuallyClaimIdPortal
        open={manuallyClaimIdPortal}
        title={"Enter Claim Id"}
        handleCancel={() => setManuallyClaimIdPortal(false)}
        handleSave={handleSaveManually}
        footer={"Save"}
        isLoading={isSubmitting}
      />

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}
    </div>
  );
};

export default React.memo(PreAuthIhx);
